/**
 * useLiveConstellation — Build a constellation from live scope assignments (DRR-075).
 *
 * Bridges the scope DAG (backend) with the constellation visualization (frontend).
 * Queries scopeAssignments for the center actor and builds a multi-ring constellation.
 *
 * Ring layout:
 *   Center = centerId actor (name from centerName prop)
 *   Ring 1 = direct ASSIGNED_TO relationships involving center actor:
 *     - Staff-centered: assignedActorIds (residents this staff cares for)
 *     - Resident-centered: inboundActorIds (staff assigned to care for this resident)
 *
 * IDs are used only internally for node keys. Display names come from nameResolver.
 * DRR-102 (fail-closed): No assignments = empty constellation (not "show all").
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type {
  ConstellationNode,
  ConstellationEdge,
  UniversalConstellationData,
} from './useUniversalConstellation';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const SCOPE_ASSIGNMENTS_QUERY = gql`
  query ScopeAssignmentsForConstellation($actorId: String!) {
    scopeAssignments(actorId: $actorId) {
      assignedActorIds
      inboundActorIds
      correlatedEntityIds
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseLiveConstellationProps {
  /** Actor ID to center the constellation on. */
  centerId: string;
  /** Display name for the center node. */
  centerName: string;
  /** Skip the query entirely (e.g., when mode !== 'myAssignments'). */
  skip?: boolean;
  /**
   * Resolve an actor sequential ID to a human-readable display name.
   * If omitted, actor IDs are used as fallback — always provide this in UI.
   */
  nameResolver?: (actorId: string) => string;
}

export interface UseLiveConstellationReturn {
  /** Constellation data compatible with UniversalConstellationGraph. */
  constellation: UniversalConstellationData | null;
  isLoading: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLiveConstellation(
  props: UseLiveConstellationProps,
): UseLiveConstellationReturn {
  const { centerId, centerName, skip = false, nameResolver } = props;
  const resolveName = (id: string) => nameResolver?.(id) ?? id;

  const { data, loading, error } = useQuery<{
    scopeAssignments: {
      assignedActorIds: string[];
      inboundActorIds: string[];
      correlatedEntityIds: string[];
    };
  }>(SCOPE_ASSIGNMENTS_QUERY, {
    variables: { actorId: centerId },
    skip: skip || !centerId,
    fetchPolicy: 'cache-and-network',
  });

  const constellation = useMemo<UniversalConstellationData | null>(() => {
    if (skip || !centerId) return null;

    const outbound = data?.scopeAssignments?.assignedActorIds ?? [];
    const inbound = data?.scopeAssignments?.inboundActorIds ?? [];

    // Staff-centered: use outbound assignments (residents assigned to this staff)
    // Resident-centered fallback: use inbound assignments (staff assigned to this resident)
    const ring1Ids = outbound.length > 0 ? outbound : inbound;

    // DRR-102 fail-closed: no assignments = empty constellation
    if (ring1Ids.length === 0) {
      return {
        center: { nodeId: centerId, name: centerName, role: 'center' },
        nodes: [{ nodeId: centerId, name: centerName, role: 'center' }],
        edges: [],
        organizationCount: 0,
        regulatoryCount: 0,
        uniqueRegulatoryCount: 0,
        sharedRegulatoryCount: 0,
      };
    }

    // Build nodes and edges
    const nodes: ConstellationNode[] = [];
    const edges: ConstellationEdge[] = [];

    // Center node
    const centerNode: ConstellationNode = {
      nodeId: centerId,
      name: centerName,
      role: 'center',
    };
    nodes.push(centerNode);

    // Ring 1 nodes (assigned entities)
    for (const id of ring1Ids) {
      nodes.push({
        nodeId: id,
        name: resolveName(id),
        role: 'organization',
        metadata: { ring: '1', edgeType: 'ASSIGNED_TO' },
      });
      edges.push({
        sourceId: centerId,
        targetId: id,
        edgeType: 'ASSIGNED_TO',
        label: 'Assigned to',
      });
    }

    return {
      center: centerNode,
      nodes,
      edges,
      organizationCount: ring1Ids.length,
      regulatoryCount: 0,
      uniqueRegulatoryCount: 0,
      sharedRegulatoryCount: 0,
    };
  }, [centerId, centerName, data, skip]);

  return {
    constellation,
    isLoading: loading,
    error: error ?? null,
  };
}
